// WHAT A RUN ACTUALLY CHANGED, fed back into its task's area lock (#5287).
//
// A coding task's `files_touched` is written ONCE, at mint, by whoever cut it — most often
// triage, which reasons about AREAS and names directories. The #513 gate
// (`provablyDisjointSql`, `@developerz/db`) then compares that declaration for the task's
// whole life, however many runs have since shown exactly which files it edits. Prod,
// tesote/tesote.ai, 2026-09-26/27: Slice 11 PR2 `tsk_4b4aadc3…` declared five
// `apps/admin/src/*` directories plus `scripts/lint`, saved 107-118 real paths on each of
// four runs, and held precise younger tasks `area_busy` for hours on directories it never
// fully used — while ALSO writing `apps/admin/test/unit/*` and `docs/architecture/*`, which
// it never declared and so never held.
//
// The run's saved diff (a commit on its own branch, `team_wip_saved`) is the one complete record of
// what it wrote, and it is the tree the next attempt resumes from. So the lock learns it
// (and, before the first edit, the run's PLAN — `plannedFilesTouched` below):
//
//   - a declared entry with an observed path STRICTLY BENEATH it is a directory the run
//     worked in, and it is replaced by what the run wrote there;
//   - every other declared entry is KEPT — a file, or a directory the run never entered.
//     The run has shown nothing about it, so nothing is narrowed on no evidence;
//   - every observed path is ADDED, inside the declaration or not — the lock is never
//     below what the run wrote.
//
// The result goes through `coveringFilesTouched`, so a set over the cap is coarsened into
// covering prefixes, never truncated; a set only the whole repository covers learns nothing
// (`[]` is an UNKNOWN radius to the gate, and a lock may not turn itself into one).
//
// WHAT IT DOES NOT PROMISE, stated because the lock is a backstop and not an enforcement:
// the next attempt may write a file in a replaced directory that no earlier run touched,
// and nothing holds that file for it. That was already true of the declaration — the team
// lane renders `files_touched` to its coordinator as ADVISORY
// (`apps/runner/src/modes/worker/team/survey.ts`), and the incident task wrote two trees it
// never declared — so this trades a guess for a record, not a guarantee for a guess. Each
// later save is learned the same way and never drops an earlier observed path (a file
// entry has nothing beneath it, so it is always kept).

import { z } from 'zod';
import {
  coveringFilesTouched,
  coversNothing,
  FILES_TOUCHED_MAX,
  FILES_TOUCHED_PATH_MAX,
} from './babysit-spec';

/** The run event that carries a saved diff, and the arm of it that landed a commit. */
const WIP_SAVED_EVENT = 'team_wip_saved';
const WIP_SAVED_KIND = 'saved';

/**
 * `team_wip_saved.changed_paths` — the saved diff's paths, as `coveringFilesTouched` bounds
 * them on the box (`apps/runner/.../team/session/resume.ts`). Complete by construction: a
 * covering set drops nothing, and a diff nothing smaller than the repository covers is not
 * sent at all.
 */
export const WipChangedPathsSchema = z
  .array(
    z
      .string()
      .min(1)
      .max(FILES_TOUCHED_PATH_MAX)
      .refine((path) => !coversNothing(path), 'a changed path must name a path'),
  )
  .min(1)
  .max(FILES_TOUCHED_MAX);

/**
 * The paths a `team_wip_saved` status payload says its commit changed, or `null` when the
 * payload is not a landed save (`kind: 'saved'`) or carries no well-formed `changed_paths`
 * — a box older than the field, or a diff too wide to cover. Box input: never throws.
 */
export function wipSavedChangedPaths(payload: unknown): string[] | null {
  if (payload === null || typeof payload !== 'object') return null;
  const row = payload as Record<string, unknown>;
  if (row['event'] !== WIP_SAVED_EVENT || row['kind'] !== WIP_SAVED_KIND) return null;
  const parsed = WipChangedPathsSchema.safeParse(row['changed_paths']);
  return parsed.success ? parsed.data : null;
}

/** The area gate's comparison form (`ADVISORY_PATH_NORMALIZE_STEPS`, `@developerz/db`). */
function normalized(path: string): string {
  return path.trim().replace(/^\.\//, '').replace(/\/+$/, '');
}

/** Is `path` strictly beneath `entry`, by path segment? */
function beneath(path: string, entry: string): boolean {
  const e = normalized(entry);
  return e !== '' && normalized(path).startsWith(`${e}/`);
}

/**
 * The task's lock after learning `observed` (see the file header), or `null` when there is
 * nothing to learn: an empty declaration (an unknown radius stays unknown — the gate already
 * serialises it, and a narrowed one would claim beside siblings no run was ever compared
 * with), an unchanged result, or a union only the whole repository covers. Pure.
 */
export function learnedFilesTouched(
  declared: readonly string[],
  observed: readonly string[],
): string[] | null {
  if (declared.length === 0 || observed.length === 0) return null;
  const kept = declared.filter((entry) => !observed.some((path) => beneath(path, entry)));
  const learned = coveringFilesTouched([...kept, ...observed]);
  if (learned.length === 0) return null;
  const before = [...new Set(declared)].sort();
  const same = before.length === learned.length && before.every((e, i) => e === learned[i]);
  return same ? null : learned;
}

/**
 * The task's lock for its EDIT phase, from the run's PLAN — the team lane's manifest, sent
 * between the read-only survey and the first edit (`files_plan`, `@developerz/nats`;
 * `lockPlannedFiles`, `@developerz/db`). {@link learnedFilesTouched}'s rule with two
 * differences, both because a plan is a different kind of evidence than a saved diff:
 *
 *   - a declared DIRECTORY the plan did not enter is DROPPED, not kept. The planner read
 *     the checkout and scoped this attempt's writes; a directory the author named as an
 *     area and the planner left alone is exactly the coarse guess that held every precise
 *     writer beneath it (plan 101, 2026-09-28: `tsk_b96a99a8…`'s `scripts/lint` froze
 *     three precise lint writers it would never touch). Only a declared entry the BOX
 *     verified is a directory in its checkout (`declaredDirs`) is dropped; a declared
 *     file, or an entry the box could not see, is kept — no evidence, no narrowing. The
 *     run's saved diff is still learned on top at its end, so what the editors wrote
 *     outside the plan is added then, as today;
 *   - an EMPTY declaration takes the plan. `[]` is an unknown radius, which is why the
 *     run-end rule refuses to narrow it; but a box-held `[]` row was admitted only with no
 *     editing sibling beside it, and this lock is taken under the claim's own lock against
 *     every sibling, so the plan is compared with every row it could meet.
 *
 * `null` when there is nothing to change: an unchanged result, or a plan only the whole
 * repository covers (a lock may not turn itself into an unknown radius). Pure.
 */
export function plannedFilesTouched(
  declared: readonly string[],
  planned: readonly string[],
  declaredDirs: readonly string[],
): string[] | null {
  if (planned.length === 0) return null;
  const dirs = new Set(declaredDirs.map(normalized));
  const kept = declared.filter(
    (entry) => !dirs.has(normalized(entry)) && !planned.some((path) => beneath(path, entry)),
  );
  const locked = coveringFilesTouched([...kept, ...planned]);
  if (locked.length === 0) return null;
  const before = [...new Set(declared)].sort();
  const same = before.length === locked.length && before.every((e, i) => e === locked[i]);
  return same ? null : locked;
}
